import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Trash2, 
  Percent, 
  Trophy, 
  FileText, 
  ArrowLeft,
  Save,
  Info,
  Gift,
  Zap,
  Star
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/authService';
import { shopService } from '../../services/shopService';
import { showSuccess, showError, showAlert } from '../../utils/alert';

const CreateCampaign = () => {
  const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const initialIdx = parseInt(searchParams.get('idx')) || 0;
    const [campaigns, setCampaigns] = useState([]);
    const [activeCampaignIdx, setActiveCampaignIdx] = useState(initialIdx);
    const [loading, setLoading] = useState(false);
    const [shop, setShop] = useState(null);
    const [activeNodeIdx, setActiveNodeIdx] = useState(0);

    useEffect(() => {
        const fetchShop = async () => {
            const currentShop = await authService.getCurrentShop();
            setShop(currentShop);
            if (currentShop?.activeCampaignIdx !== undefined) {
                setActiveNodeIdx(currentShop.activeCampaignIdx);
            }
            if (currentShop?.campaigns?.length > 0) {
                setCampaigns(currentShop.campaigns);
                // If initialIdx is within bounds, use it
                if (initialIdx < currentShop.campaigns.length) {
                    setActiveCampaignIdx(initialIdx);
                }
            } else {
                // Initialize with one empty campaign if none exist
                setCampaigns([{
                    name: 'Default Campaign',
                    type: 'Elite Golden Card (Standard)',
                    rewards: []
                }]);
            }
        };
        fetchShop();
    }, []);

    const activeCampaign = campaigns[activeCampaignIdx] || { rewards: [] };

    const addCampaign = () => {
        const plan = shop?.plan || 'STARTER';
        if (plan === 'STARTER' && campaigns.length >= 1) {
            showError('Plan Limit', 'Starter plan only allows 1 Stamp QR.');
            return;
        }
        if (plan === 'PREMIUM' && campaigns.length >= 2) {
            showError('Plan Limit', 'Premium plan only allows 1 Stamp QR and 1 Scratch QR.');
            return;
        }

        const newCamp = {
            name: `Campaign #${campaigns.length + 1}`,
            type: 'Elite Golden Card (Standard)',
            mode: plan === 'STARTER' ? 'stamp' : 'scratch',
            rewards: [{ name: '', probability: 100, icon: '🎁' }]
        };
        setCampaigns([...campaigns, newCamp]);
        setActiveCampaignIdx(campaigns.length);
    };

    const updateActiveCampaign = (field, value) => {
        const updated = [...campaigns];
        updated[activeCampaignIdx] = { ...updated[activeCampaignIdx], [field]: value };
        setCampaigns(updated);
    };

    const addReward = () => {
        const plan = shop?.plan || 'STARTER';
        const currentRewards = campaigns[activeCampaignIdx].rewards || [];
        
        if (plan === 'PREMIUM' && currentRewards.length >= 3) {
            showError('Plan Limit', 'Premium plan allows maximum 3 rewards per scratch card.');
            return;
        }

        const updated = [...campaigns];
        updated[activeCampaignIdx].rewards.push({ name: '', probability: 0, icon: '🎁' });
        setCampaigns(updated);
    };

    const removeReward = (idx) => {
        const updated = [...campaigns];
        updated[activeCampaignIdx].rewards.splice(idx, 1);
        setCampaigns(updated);
    };

    const handleRewardChange = (idx, field, value) => {
        const updated = [...campaigns];
        updated[activeCampaignIdx].rewards[idx][field] = value;
        setCampaigns(updated);
    };

    const totalProb = activeCampaign.rewards.reduce((acc, curr) => acc + (Number(curr.probability) || 0), 0);
    const handleSave = async () => {
        const plan = shop?.plan || 'STARTER';
        
        // Final Validation before save
        for (const camp of campaigns) {
            if (plan === 'STARTER') {
                if (camp.mode !== 'stamp') {
                    showError('Validation Error', 'Starter plan only supports Stamp Protocol.');
                    return;
                }
                if (camp.totalStamps > 5) {
                    showError('Validation Error', 'Starter plan allows maximum 5 stamps.');
                    return;
                }
            }
            if (plan === 'PREMIUM') {
                if (camp.mode === 'stamp' && camp.totalStamps > 5) {
                    showError('Validation Error', 'Premium plan allows maximum 5 stamps.');
                    return;
                }
                if (camp.mode === 'scratch' && camp.rewards.length > 3) {
                    showError('Validation Error', 'Premium plan allows maximum 3 scratch cards.');
                    return;
                }
            }
        }

        const stampCount = campaigns.filter(c => c.mode === 'stamp').length;
        const scratchCount = campaigns.filter(c => c.mode === 'scratch').length;

        if (plan === 'PREMIUM') {
            if (stampCount > 1 || scratchCount > 1) {
                showError('Validation Error', 'Premium plan allows only 1 Stamp QR and 1 Scratch QR.');
                return;
            }
        }

        setLoading(true);
        try {
            await shopService.updateCampaigns(shop.id, campaigns);
            showSuccess('Success', "All campaigns updated successfully!");
            navigate('/admin/dashboard');
        } catch (error) {
            console.error(error);
            showError('Error', "Error updating campaigns");
        } finally {
            setLoading(false);
        }
    };

    const handleSetActive = async (index) => {
        setLoading(true);
        try {
            await shopService.updateActiveCampaign(shop.id, index);
            setActiveNodeIdx(index);
            showSuccess('Active Protocol Set', `"${campaigns[index].name}" is now the live reward node.`);
        } catch (error) {
            showError('Error', 'Failed to set active protocol');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#fdfdfd] pt-32 pb-12 px-8 flex justify-center overflow-y-auto relative">
            <div className="absolute inset-0 luxury-grid-bg pointer-events-none" />
            
            <div className="max-w-6xl w-full relative z-10">
                <motion.button
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    onClick={() => navigate('/admin/dashboard')}
                    className="flex items-center gap-2 text-[#999] hover:text-black transition-colors text-[10px] font-bold uppercase tracking-widest mb-12"
                >
                    <ArrowLeft size={16} />
                    Return to Dashboard
                </motion.button>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    {/* Left Sidebar: Campaign List */}
                    <div className="lg:col-span-4 space-y-6">
                        <div className="luxury-card p-8">
                            <div className="flex items-center justify-between mb-8">
                                <h3 className="text-xs font-bold text-black uppercase tracking-widest">Active Sets</h3>
                                <button onClick={addCampaign} className="p-2 bg-black text-[#d4af37] rounded-xl hover:bg-[#d4af37] hover:text-white transition-all shadow-lg">
                                    <Plus size={16} />
                                </button>
                            </div>
                            <div className="space-y-3">
                                {campaigns.map((camp, i) => (
                                    <div
                                        key={i}
                                        className={`w-full p-4 rounded-2xl border transition-all relative group ${activeCampaignIdx === i
                                            ? 'bg-black border-black text-[#d4af37] shadow-xl'
                                            : 'bg-white border-black/[0.05] text-[#999] hover:border-black/20 shadow-sm'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-[9px] font-black uppercase tracking-widest">{camp.type.split(' ')[0]}</p>
                                            
                                            {/* Luxury Toggle Switch */}
                                            <button 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSetActive(i);
                                                }}
                                                className={`w-10 h-5 rounded-full transition-all relative ${activeNodeIdx === i ? 'bg-[#d4af37]' : 'bg-black/10'}`}
                                            >
                                                <motion.div 
                                                    animate={{ x: activeNodeIdx === i ? 20 : 2 }}
                                                    className="absolute top-1 w-3 h-3 bg-white rounded-full shadow-sm"
                                                />
                                            </button>
                                        </div>
                                        
                                        <button 
                                            onClick={() => setActiveCampaignIdx(i)}
                                            className="w-full text-left"
                                        >
                                            <p className="text-sm font-bold truncate pr-10">{camp.name}</p>
                                        </button>
                                        
                                        {activeNodeIdx === i && (
                                            <div className="mt-2 flex items-center gap-1.5 text-[8px] font-black uppercase tracking-widest text-[#d4af37]/60">
                                                <Zap size={8} fill="currentColor" /> Live Node
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Right Area: Campaign Editor */}
                    <div className="lg:col-span-8">
                        <motion.div
                            key={activeCampaignIdx}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="luxury-card p-12"
                        >
                            <div className="mb-12">
                                <h1 className="text-3xl font-serif text-black mb-2">Architecting: <span className="luxury-text italic">{activeCampaign.name}</span></h1>
                                <p className="text-[#666] text-[10px] font-bold uppercase tracking-[0.3em]">Configure reward mechanics for this specific card set</p>
                            </div>

                            <div className="space-y-12">
                                {/* Protocol Selection (Luxury Toggle) */}
                                <div className="space-y-6 pb-12 border-b border-black/5">
                                    <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Select Campaign Protocol</label>
                                    <div className={`grid ${shop?.plan === 'STARTER' ? 'grid-cols-1' : 'grid-cols-2'} gap-6`}>
                                        {shop?.plan !== 'STARTER' && (
                                            <button 
                                                onClick={() => {
                                                    updateActiveCampaign('mode', 'scratch');
                                                }}
                                                className={`p-8 rounded-[2rem] border-2 transition-all text-left flex items-start gap-5 ${
                                                    (activeCampaign.mode || 'scratch') === 'scratch'
                                                        ? 'bg-black border-black text-[#d4af37] shadow-2xl'
                                                        : 'bg-white border-black/[0.05] text-[#999] hover:border-black/10'
                                                }`}
                                            >
                                                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
                                                    (activeCampaign.mode || 'scratch') === 'scratch' ? 'bg-[#d4af37] text-black' : 'bg-black/5 text-[#ccc]'
                                                }`}>
                                                    <Zap size={24} />
                                                </div>
                                                <div>
                                                    <h4 className={`text-sm font-bold uppercase tracking-widest mb-1 ${
                                                        (activeCampaign.mode || 'scratch') === 'scratch' ? 'text-white' : 'text-black'
                                                    }`}>Scratch System</h4>
                                                    <p className="text-[10px] leading-relaxed opacity-60">Classic instant-win sequential rewards</p>
                                                </div>
                                            </button>
                                        )}

                                        <button 
                                            onClick={() => updateActiveCampaign('mode', 'stamp')}
                                            className={`p-8 rounded-[2rem] border-2 transition-all text-left flex items-start gap-5 ${
                                                activeCampaign.mode === 'stamp'
                                                    ? 'bg-black border-black text-[#d4af37] shadow-2xl'
                                                    : 'bg-white border-black/[0.05] text-[#999] hover:border-black/10'
                                            }`}
                                        >
                                            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
                                                activeCampaign.mode === 'stamp' ? 'bg-[#d4af37] text-black' : 'bg-black/5 text-[#ccc]'
                                            }`}>
                                                <Star size={24} />
                                            </div>
                                            <div>
                                                <h4 className={`text-sm font-bold uppercase tracking-widest mb-1 ${
                                                    activeCampaign.mode === 'stamp' ? 'text-white' : 'text-black'
                                                }`}>Stamp Loyalty</h4>
                                                <p className="text-[10px] leading-relaxed opacity-60">Collect stamps to unlock master prizes</p>
                                            </div>
                                        </button>
                                    </div>
                                </div>

                                {/* Configuration Detail */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-12 border-b border-black/5">
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Card Display Name</label>
                                        <input
                                            type="text"
                                            value={activeCampaign.name}
                                            onChange={(e) => updateActiveCampaign('name', e.target.value)}
                                            className="w-full bg-black/[0.02] border border-black/[0.05] rounded-2xl py-4 px-6 text-sm text-black focus:outline-none focus:border-[#d4af37]/50 transition-all"
                                        />
                                    </div>
                                    <div className="space-y-3">
                                        <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Visual Architecture</label>
                                        <select
                                            value={activeCampaign.type}
                                            onChange={(e) => updateActiveCampaign('type', e.target.value)}
                                            className="w-full bg-black/[0.02] border border-black/[0.05] rounded-2xl py-4 px-6 text-sm text-black focus:outline-none focus:border-[#d4af37]/50 transition-all appearance-none cursor-pointer"
                                        >
                                            <option className="bg-white">Elite Golden Card (Standard)</option>
                                            <option className="bg-white">Silver Stealth Card</option>
                                            <option className="bg-white">Diamond Prism Card</option>
                                            <option className="bg-white">Royal Velvet Card</option>
                                        </select>
                                    </div>
                                    {activeCampaign.mode === 'stamp' && (
                                        <div className="space-y-3">
                                            <label className="text-[10px] font-bold text-[#999] uppercase tracking-widest ml-1">Total Stamps Required (Max 5)</label>
                                            <input
                                                type="number"
                                                value={activeCampaign.totalStamps === undefined ? '' : activeCampaign.totalStamps}
                                                onChange={(e) => {
                                                    const val = e.target.value === '' ? '' : parseInt(e.target.value);
                                                    if (shop?.plan === 'STARTER' && val > 5) return;
                                                    updateActiveCampaign('totalStamps', val);
                                                }}
                                                className="w-full bg-black/[0.02] border border-black/[0.05] rounded-2xl py-4 px-6 text-sm text-black focus:outline-none focus:border-[#d4af37]/50 transition-all"
                                                min="1"
                                                max={shop?.plan === 'STARTER' ? "5" : "50"}
                                                placeholder="5"
                                            />
                                        </div>
                                    )}
                                </div>

                                {activeCampaign.mode === 'stamp' ? (
                                    /* Stamp Card Config */
                                    <div className="space-y-8">
                                        <div className="flex items-center gap-3">
                                            <Trophy size={16} className="text-black" />
                                            <h3 className="text-sm font-bold uppercase tracking-widest text-black">Final Stamp Reward</h3>
                                        </div>
                                        <div className="grid grid-cols-12 gap-6 items-center bg-black/[0.02] p-8 rounded-3xl border border-black/[0.05]">
                                            <div className="col-span-3 md:col-span-2">
                                                <label className="block text-[8px] font-black text-[#ccc] uppercase tracking-widest mb-2 text-center">Reward Icon</label>
                                                <input
                                                    className="w-full bg-white border border-black/[0.05] rounded-xl py-3 text-2xl text-center focus:outline-none focus:border-black/20 shadow-sm"
                                                    value={activeCampaign.finalReward?.icon || '🎁'}
                                                    onChange={(e) => updateActiveCampaign('finalReward', { ...activeCampaign.finalReward, icon: e.target.value })}
                                                />
                                            </div>
                                            <div className="col-span-9 md:col-span-10">
                                                <label className="block text-[8px] font-black text-[#ccc] uppercase tracking-widest mb-2 ml-1">Final Prize Description</label>
                                                <input
                                                    className="w-full bg-white border border-black/[0.05] rounded-xl py-4 px-6 text-sm text-black focus:outline-none focus:border-black/20 shadow-sm"
                                                    placeholder="e.g. 1 Free Coffee + Custom Mug"
                                                    value={activeCampaign.finalReward?.name || ''}
                                                    onChange={(e) => updateActiveCampaign('finalReward', { ...activeCampaign.finalReward, name: e.target.value })}
                                                />
                                            </div>
                                        </div>
                                        <div className="p-6 rounded-[2rem] bg-black/[0.01] border border-black/[0.03] flex items-center gap-3 text-[9px] font-bold uppercase tracking-[0.3em] text-[#bbb]">
                                            <Info size={14} />
                                            Stamp Logic: Customer earns 1 stamp per QR scan until total is reached.
                                        </div>
                                    </div>
                                ) : (
                                    /* Rewards List (Scratch Mode) */
                                    shop?.plan === 'STARTER' ? (
                                        <div className="p-12 text-center luxury-card border-dashed border-2 border-rose-500/20 bg-rose-500/5">
                                            <Zap size={32} className="mx-auto mb-4 text-rose-500 opacity-20" />
                                            <h4 className="text-lg font-serif text-black mb-2">Access Restricted</h4>
                                            <p className="text-[10px] font-bold text-[#999] uppercase tracking-widest">Starter plan does not support Scratch Protocols.</p>
                                        </div>
                                    ) : (
                                        <div className="space-y-8">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3">
                                                    <Gift size={16} className="text-black" />
                                                    <h3 className="text-sm font-bold uppercase tracking-widest text-black">Tiered Rewards</h3>
                                                </div>
                                                <button onClick={addReward} className="text-[10px] font-bold text-[#d4af37] hover:underline uppercase tracking-widest flex items-center gap-2">
                                                    <Plus size={14} /> Add Reward
                                                </button>
                                            </div>

                                            <div className="space-y-6">
                                                {activeCampaign.rewards.map((reward, i) => (
                                                    <motion.div 
                                                        key={i} 
                                                        layout
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        className="group relative"
                                                    >
                                                        <div className="grid grid-cols-12 gap-6 items-center bg-black/[0.02] hover:bg-black/[0.04] p-6 rounded-3xl border border-black/[0.05] transition-all">
                                                            {/* Icon/Emoji Picker */}
                                                            <div className="col-span-2 md:col-span-1">
                                                                <label className="block text-[8px] font-black text-[#ccc] uppercase tracking-widest mb-2 text-center">Icon</label>
                                                                <input
                                                                    className="w-full bg-white border border-black/[0.05] rounded-xl py-3 text-2xl text-center focus:outline-none focus:border-black/20 shadow-sm"
                                                                    value={reward.icon}
                                                                    onChange={(e) => handleRewardChange(i, 'icon', e.target.value)}
                                                                    placeholder="🎁"
                                                                />
                                                            </div>

                                                            {/* Reward Description */}
                                                            <div className="col-span-7 md:col-span-8">
                                                                <label className="block text-[8px] font-black text-[#ccc] uppercase tracking-widest mb-2 ml-1">Reward Description</label>
                                                                <input
                                                                    className="w-full bg-white border border-black/[0.05] rounded-xl py-4 px-6 text-sm text-black focus:outline-none focus:border-black/20 shadow-sm placeholder-[#ddd]"
                                                                    placeholder="e.g. ₹500 Exclusive Gift Card"
                                                                    value={reward.name}
                                                                    onChange={(e) => handleRewardChange(i, 'name', e.target.value)}
                                                                />
                                                            </div>

                                                            {/* Probability Percentage */}
                                                            <div className="col-span-2 md:col-span-2">
                                                                <label className="block text-[8px] font-black text-[#ccc] uppercase tracking-widest mb-2 ml-1">Chance (%)</label>
                                                                <div className="relative">
                                                                    <input
                                                                        type="number"
                                                                        className="w-full bg-white border border-black/[0.05] rounded-xl py-4 px-6 pr-10 text-sm text-black focus:outline-none focus:border-black/20 shadow-sm"
                                                                        value={reward.probability}
                                                                        onChange={(e) => handleRewardChange(i, 'probability', e.target.value)}
                                                                    />
                                                                    <Percent className="absolute right-4 top-1/2 -translate-y-1/2 text-[#ccc]" size={14} />
                                                                </div>
                                                            </div>

                                                            {/* Action: Delete */}
                                                            <div className="col-span-1 flex flex-col items-center justify-end h-full pb-4">
                                                                <button 
                                                                    onClick={() => removeReward(i)} 
                                                                    className="p-3 text-[#ccc] hover:text-rose-500 hover:bg-rose-50/50 rounded-xl transition-all"
                                                                >
                                                                    <Trash2 size={18} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                ))}
                                            </div>

                                            <div className="p-6 rounded-[2rem] bg-black/[0.01] border border-black/[0.03] flex items-center justify-between text-[9px] font-bold uppercase tracking-[0.3em] text-[#bbb]">
                                                <div className="flex items-center gap-3">
                                                    <Info size={14} />
                                                    Active Logic: Relative Weighting System
                                                </div>
                                                <span className="italic">No Requirements Applied</span>
                                            </div>
                                        </div>
                                    )
                                )}

                                <button
                                    onClick={handleSave}
                                    disabled={loading}
                                    className="luxury-button w-full flex items-center justify-center gap-3 !py-5"
                                >
                                    {loading ? 'Archiving...' : <><Save size={18} /> Deploy All Configurations</>}
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreateCampaign;
